import { useState, useEffect } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { Menu, X, ArrowRight, Github, Linkedin, Mail } from 'lucide-react'
import logo from '../Images/LogoOM.PNG'

export default function Navbar() {
  const location = useLocation()
  const [isOpen, setIsOpen] = useState(false)
  const [scrolled, setScrolled] = useState(false)

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50)
    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: "Ventures", path: "/ventures" },
    { name: 'Journey', path: '/journey' },
    { name: 'Expertise', path: '/expertise' },
    { name: 'Projects', path: '/projects' },
    { name: 'Services', path: '/services' },
    { name: 'Tools', path: '/tools' },
  ];

  return (
    <>
      {/* MAIN NAV BAR */}
      <nav className={`fixed top-0 left-0 w-full z-[100] transition-all duration-500 ${
        scrolled ? 'bg-white py-3 shadow-sm' : 'bg-transparent py-6'
      }`}>
        <div className="max-w-[1800px] mx-auto px-6 md:px-12 flex justify-between items-center">
          
          {/* 1. BRANDING */}
          <Link to="/" className="flex items-center gap-4 group">
            <div className="w-10 h-10 border-2 border-slate-900 flex items-center justify-center rotate-45 group-hover:rotate-0 transition-transform duration-500">
              <img src={logo} alt="L" className="w-6 h-6 -rotate-45 group-hover:rotate-0 transition-transform duration-500 object-contain" />
            </div>
            <div className="hidden sm:block">
              <span className="block text-sm font-black uppercase tracking-[0.3em] text-slate-950">Omkar More</span>
              <span className="block text-[9px] font-bold text-cyan-600 uppercase tracking-widest">Digital & NDT Engineer</span>
            </div>
          </Link>

          {/* 2. DESKTOP CENTER NAVIGATION (Minimalist Underline Style) */}
          <div className="hidden lg:flex items-center gap-10">
            {navLinks.slice(0, 5).map((link) => (
              <Link
                key={link.name}
                to={link.path}
                className={`group relative text-[11px] font-black uppercase tracking-[0.2em] transition-colors ${
                  location.pathname === link.path ? 'text-cyan-600' : 'text-slate-500 hover:text-slate-950'
                }`}
              >
                {link.name}
                <span className={`absolute -bottom-1 left-0 h-[2px] bg-cyan-600 transition-all duration-300 ${
                  location.pathname === link.path ? 'w-full' : 'w-0 group-hover:w-full'
                }`} />
              </Link>
            ))}
          </div>

          {/* 3. RIGHT SIDE: MENU TRIGGER & CTA */}
          <div className="flex items-center gap-6">
            <Link 
              to="/contact" 
              className="hidden md:flex items-center gap-3 group text-[11px] font-black uppercase tracking-widest text-slate-950"
            >
              Start Project
              <div className="w-8 h-8 rounded-full border border-slate-200 flex items-center justify-center group-hover:bg-slate-950 group-hover:text-white transition-all">
                <ArrowRight size={14} />
              </div>
            </Link>

            <button 
              onClick={() => setIsOpen(true)}
              className="flex items-center gap-3 p-2 group"
            >
              <span className="hidden lg:block text-[11px] font-black uppercase tracking-widest text-slate-500 group-hover:text-slate-950">Menu</span>
              <div className="flex flex-col gap-1">
                <span className="w-6 h-[2px] bg-slate-950" />
                <span className="w-4 h-[2px] bg-slate-950 self-end" />
              </div>
            </button>
          </div>
        </div>
      </nav>

      {/* FULL SCREEN OVERLAY MENU */}
      <div className={`fixed inset-0 z-[110] bg-slate-950 transition-all duration-700 ease-[cubic-bezier(0.85,0,0.15,1)] ${
        isOpen ? 'translate-y-0' : '-translate-y-full'
      }`}>
        {/* Close Button */}
        <button 
          onClick={() => setIsOpen(false)}
          className="absolute top-8 right-8 text-white/50 hover:text-white flex items-center gap-3 uppercase text-[10px] tracking-[0.5em] font-black transition-colors"
        >
          Close <X size={32} strokeWidth={1} />
        </button>

        <div className="h-full flex flex-col lg:flex-row">
          {/* LEFT SIDE: Visual/Info (Desktop only) */}
          <div className="hidden lg:flex w-1/3 border-r border-white/10 p-20 flex-col justify-between">
            <div className="space-y-6">
              <img src={logo} alt="Logo" className="w-20 opacity-20" />
              <p className="text-slate-500 text-sm leading-relaxed max-w-xs font-medium">
                Merging industrial NDT precision with modern digital infrastructure. Based in India, operating globally.
              </p>
            </div>
            
            <div className="space-y-4">
              <div className="text-[10px] text-cyan-500 font-black uppercase tracking-widest">Connect</div>
<div className="flex gap-6">
  {/* GitHub */}
  <a
    href="https://github.com/OmkarMore278"
    target="_blank"
    rel="noopener noreferrer"
    aria-label="GitHub Profile"
    className="text-white hover:text-cyan-500 transition-colors"
  >
    <Github size={20} />
  </a>

  {/* LinkedIn */}
  <a
    href="https://www.linkedin.com/in/omkar-more-379984214/"
    target="_blank"
    rel="noopener noreferrer"
    aria-label="LinkedIn Profile"
    className="text-white hover:text-cyan-500 transition-colors"
  >
    <Linkedin size={20} />
  </a>

  {/* Email */}
  <a
    href="mailto:moreomkar972@gmail.com"
    aria-label="Send Email"
    className="text-white hover:text-cyan-500 transition-colors"
  >
    <Mail size={20} />
  </a>
</div>

            </div>
          </div>

          {/* RIGHT SIDE: BIG LINKS */}
          <div className="flex-1 flex items-center justify-center p-6">
            <ul className="space-y-4 md:space-y-8 text-center lg:text-left w-full max-w-2xl">
              {navLinks.map((link, i) => (
                <li key={link.name} className="overflow-hidden">
                  <Link
                    to={link.path}
                    onClick={() => setIsOpen(false)}
                    className="group flex items-center justify-center lg:justify-start gap-6"
                  >
                    <span className="text-cyan-800 font-mono text-xl group-hover:text-cyan-400 transition-colors italic">0{i + 1}</span>
                    <span className="text-4xl md:text-7xl font-black text-white uppercase tracking-tighter group-hover:italic group-hover:text-cyan-500 transition-all duration-300">
                      {link.name}
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </>
  )
}